/**
 * The island's transient "{agentName} started working" announcement.
 *
 * Driven by the GLOBAL in-motion COUNT (the `agent-workspace-tasks` aggregate), never by an id-diff of the
 * task LIST: the aggregate is loaded for the pill itself, so a baseline exists before any new task, whereas
 * the list loads lazily and its first load often coincides with the task-creation event — which would seed
 * the new task silently and never fire. Native learned that the hard way; the comment is kept here because
 * the cheaper-looking id-diff is exactly the wrong instinct.
 *
 * Pure + unit-testable. Shared 1:1 with native `agentStartedAnnouncement` (Android `Models.kt`, iOS
 * `SymphonyData.agentStartedAnnouncement`).
 */

/** The one status native counts as "an agent is working on it right now". */
const IN_PROGRESS = 'IN_PROGRESS';

export interface AgentStartedInput {
  /** The last count observed. `null` on the FIRST observation, which seeds silently. */
  prevInMotionCount: number | null;
  inMotionCount: number;
  /** The screen task list — only used to NAME the agent, never to detect the start. */
  tasks: { status?: string; updatedDate?: string | Date; agentId?: string }[];
  agents: { id?: string; name?: string }[];
  /** The fallback when the newest task's agent can't be resolved to a roster name — the Maestro. */
  mainAgentName: string;
}

export interface AgentStartedAnnouncement {
  /** The name to announce, or null when nothing started. */
  name: string | null;
  /** The single started agent's own id for the pill's LEADING GLYPH — a real roster id when the newest
   *  in-progress task resolved to a roster name, else `''` (the Maestro / agent-less bucket, drawn as the
   *  Maestro mark), matching the `name` fallback. `null` when nothing started. Shared 1:1 with native's
   *  `agentStartedAnnouncement` third return (iOS/Android `agentStartedAgentId`) so the flash shows the
   *  ONE started agent, never the general working cluster. */
  agentId: string | null;
  /** The count to remember as the next baseline — returned even when nothing is announced. */
  count: number;
}

function updatedMs(value: string | Date | undefined): number {
  if (!value) return 0;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

export function agentStartedAnnouncement(input: AgentStartedInput): AgentStartedAnnouncement {
  const { prevInMotionCount, inMotionCount, tasks, agents, mainAgentName } = input;
  // A first observation seeds the baseline and announces nothing, so whatever was already running when the
  // app opened stays quiet.
  if (prevInMotionCount === null) return { name: null, agentId: null, count: inMotionCount };
  // Only a RISE means an agent started. A fall (a task finishing) and a flat count are both silent.
  if (inMotionCount <= prevInMotionCount) return { name: null, agentId: null, count: inMotionCount };

  const names = new Map<string, string>();
  for (const agent of agents) {
    const name = agent.name?.trim();
    if (agent.id && name) names.set(agent.id, name);
  }
  const newest = tasks
    .filter((task) => task.status === IN_PROGRESS)
    .reduce<(typeof tasks)[number] | undefined>(
      (best, task) => (best === undefined || updatedMs(task.updatedDate) > updatedMs(best.updatedDate) ? task : best),
      undefined,
    );
  const newestAgentId = newest?.agentId || undefined;
  const resolved = newestAgentId ? names.get(newestAgentId) : undefined;
  // The pill's leading dot follows the NAME resolution: a resolved roster agent shows its own dot (its real
  // id); an unresolved / agent-less start is the Maestro ('' → the Maestro mark), matching the name's
  // `mainAgentName` fallback. Shared 1:1 with native (`resolved != nil ? newestAgentId : ""`).
  return { name: resolved || mainAgentName, agentId: resolved ? newestAgentId! : '', count: inMotionCount };
}

/** How long the announcement holds the pill before the colour states resume. Native's `showAgentStartedPill`. */
export const AGENT_STARTED_MS = 5_000;
